import { useFormik } from "formik";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import TextField from "../components/common/TextField";
import Container from "../components/common/Container";
import FieldErrorMsg from "../components/common/FieldErrorMsg";
import {
  ACCEPT_IMAGE_FILES,
  EDITOR_LIMITS,
  UN_REGISTERED,
  URL_REGEX,
} from "../Constants";
import ExternalLinkField from "../components/common/ExternalLinkField";
import { ReactComponent as PlusIcon } from "../assets/img/common/plus-icon.svg";
import CropImageModal from "../components/common/CropImgModal";
import handleCropImgChange from "../utils/handleCropImgChange";
import { ReactComponent as RemoveRowIcon } from "../assets/img/common/trash-red-icon.svg";
import { ReactComponent as ProfileEdtiIcon } from "../assets/img/common/profile-edit-icon.svg";
import { useEffect } from "react";
import AttachFileField from "../components/common/AttachFileFiled";
import notify from "../components/common/notify";
import useApi, { handleError } from "../hooks/useApi";
import {
  useGetProjectByIdQuery,
  useLazyGetProjectByIdQuery,
} from "../redux/slices/api/projectsApiSlice";
import {
  useChangeResultVisibilityMutation,
  useEditResultMutation,
  useGetResultByIdQuery,
  useSaveResultMutation,
} from "../redux/slices/api/resultApiSlice";
import { Bars, Oval } from "react-loader-spinner";
import { setResultEditor } from "../redux/slices/generalSlice";
import { useDispatch } from "react-redux";
import useResetForm from "../hooks/useResetForm";
import { convertHtmlToText } from "../utils/general";
import useNavigateToProfile from "../hooks/useNavigateToProfile";
import ExternalLink from "../components/common/ExternalLink";
import LexicalEditor from "../components/common/LexicalEditor";
import InputTag from "../components/common/InputTag";
import { KEYWORD_SUGGESTIONS } from "../Constants/suggestions";
import { resultTypes } from "../data/dataCheckboxes";
import MetaTags from "../components/common/MetaTags";
import CopyContent from "../components/common/CopyContent";
import WalletDisplay from "../components/common/WalletDisplay";
const CreateResult = () => {
  const [searchParam, setSearchParam] = useSearchParams();
  let project_id_param = searchParam.get("project-id");
  let result_id_params = searchParam.get("result-id");
  const [resultId, setResultId] = useState(result_id_params);
  const [isSaved, setIsSaved] = useState(false);
  const [isPublish, setIsPublish] = useState(false);
  const [saveAndPublishLoading, setSaveAndPublishLoading] = useState(false);
  useEffect(() => {
    setResultId(result_id_params);
  }, [result_id_params]);
  const navigate = useNavigate();
  const { navigateToProfile } = useNavigateToProfile();

  const {
    isLoading: projectDataLoading,
    data: projectData,
    refetch: refetchProjectData,
  } = useGetProjectByIdQuery(
    { id: project_id_param },
    {
      skip: !project_id_param,
    }
  );
  const { project } = projectData ?? {};
  const { isLoading: resultDataLoading, data } = useGetResultByIdQuery(
    { id: result_id_params },
    {
      skip: !result_id_params,
    }
  );
  const { result: resultData } = data ?? {};
  const [saveResult, { isLoading: saveDraftLoading }] = useSaveResultMutation();
  const [changeResultVisibility, { isLoading: changeResultVisibilityLoading }] =
    useChangeResultVisibilityMutation();
  const [editResult, { isLoading: editResultLoading }] =
    useEditResultMutation();
  const [triggerGetProjectData] = useLazyGetProjectByIdQuery();

  const dispatch = useDispatch();

  const handleSubmit = (values) => {
    handleSaveAndPublishResult(!resultData?.visible && isPublish);
  };
  const formik = useFormik({
    initialValues: {
      title: "",
      feature_image: "",
      links: [""],
      attachment: "",
      result: searchParam.get("result-id") ? "" : " ",
      termsOfService: false,
      premium: false,
      result_summary: "",
      types: [],
      keywords: [],
    },
    onSubmit: (values) => {
      handleSubmit(values);
    },

    validationSchema: yup
      .object()
      .shape({
        title: yup
          .string()
          .required("title is required")
          .min(4, "title can not be lower than 4 characters")
          .max(200, "title can not be more than 200 characters"),
        result: yup
          .string()
          .required("result is required!")
          .test(
            "is-lower-than-50-characters",
            "result can not be lower than 50 characters!",
            function (value, context) {
              let summary = convertHtmlToText({ htmlContent: value });
              if (summary?.length < 50) return false;
              return true;
            }
          )
          .max(
            EDITOR_LIMITS.result,
            `result can not be more than ${EDITOR_LIMITS.result} characters`
          ),
        feature_image: yup
          .string()
          .required("result image is required")
          .matches(URL_REGEX, "result image must be a valid url"),
        links: yup
          .array()
          .of(
            yup
              .string()
              .matches(URL_REGEX, "link items must be a valid url")
              .min(5, "link items can not be lower than 5 characters")
              .max(100, "link items can not be more than 100 characters")
          ),
        keywords: yup
          .array()
          .required("keywords are required")
          .min(1, "keywords must have at least 1 item"),
        types: yup
          .array()
          .required("result type is required")
          .min(1, "result type must have at least 1 item"),
        attachment: yup
          .string()
          .matches(URL_REGEX, "attachment must be a valid url")
          .min(30, "attachment url can not be lower than 30 characters")
          .max(150, "attachment url can not be more than 150 characters"),
        termsOfService:
          resultData?.visible || !isPublish
            ? yup.boolean()
            : yup
                .boolean()
                .required("The terms and conditions must be accepted.")
                .oneOf([true], "The terms and conditions must be accepted."),
      })
      .noUnknown(true),
  });

  // Reset the form on mount, setting initial values and clearing the result editor state
  useResetForm({
    onMount: () => {
      formik?.setValues(formik.initialValues);
      dispatch(setResultEditor(""));
    },
  });

  // State to manage visibility of result image cropping modal
  const [showResultCropModal, setShowResultCropModal] = useState(false);

  // State to store the result image for cropping
  const [cropResultImage, setCropResultImage] = useState(
    formik?.values?.feature_image || null
  );

  // State to store the selected result image
  const [resultImg, setResultImg] = useState(
    formik?.values?.feature_image || null
  );

  // Event handler for selecting an image and initiating the cropping process
  const handleImageSelect = (e) => {
    handleCropImgChange(e, setResultImg, setShowResultCropModal);
  };

  // Ensure that the cropResultImage and resultImg states are set if a feature image exists
  useEffect(() => {
    if (formik?.values?.feature_image && !cropResultImage && !resultImg) {
      setCropResultImage(formik?.values?.feature_image);
      setResultImg(formik?.values?.feature_image);
    }
  }, [formik?.values?.feature_image, cropResultImage, resultImg]);

  // Use API hook to handle file uploads
  const { loading: uploadFileLoading, PUBLIC_REQUEST } = useApi();

  // Event handler for attaching a file and updating formik values
  const handleAttachFileChange = async (e) => {
    let formData = new FormData();
    formData.append("file", e.target.files[0]);

    // Define success and error callbacks for file upload
    const onSuccess = (data) => {
      notify(data?.message, "success");
      formik.setFieldValue("attachment", data?.link);
    };
    const onError = (error) => {
      if (error.status === 413) {
        notify(
          "The size of the uploaded file is large, please try again.",
          "error"
        );
      }
    };

    // Make a public request to upload the file
    await PUBLIC_REQUEST({
      url: "/upload/",
      method: "post",
      data: formData,
      notifyError: true,
      onSuccess,
      onError,
    });
  };

  // Recursive function to get the first error key in a nested object
  const getFirstErrorKey = (object, keys = []) => {
    const firstErrorKey = Object.keys(object)[0];
    if (object?.[firstErrorKey] && typeof object[firstErrorKey] === "object") {
      return getFirstErrorKey(object[firstErrorKey], [...keys, firstErrorKey]);
    }
    return [...keys, firstErrorKey].join(".");
  };

  // Scroll to the first formik error element on submission if there are validation errors
  useEffect(() => {
    if (!formik.isValid && formik.submitCount !== 0 && formik.isSubmitting) {
      const firstErrorKey = getFirstErrorKey(formik.errors);
      let element = document.getElementById(firstErrorKey);

      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }
  }, [formik.submitCount, formik.isValid, formik.errors, formik.isSubmitting]);

  // Function to set formik fields based on result data
  const setProjectFields = () => {
    if (resultId) {
      let keywords = resultData?.keywords?.map((keyword) => ({
        id: keyword,
        text: keyword,
      }));
      formik.setValues({
        ...formik?.values,
        ...resultData,
        keywords,
        termsOfService: resultData?.visible,
      });
      dispatch(setResultEditor(resultData?.result));
    }
  };

  // Use effect to set formik fields when result data is loaded
  useEffect(() => {
    if (!resultDataLoading && resultData) {
      setProjectFields();
    }
    //eslint-disable-next-line
  }, [resultDataLoading, resultData]);

  // Function to handle saving and publishing a result
  const handleSaveAndPublishResult = async (isPublish) => {
    // Initialize empty objects to store body and data
    let body = {};
    let data = {};

    // Filter non-empty links from the formik values
    let links = formik.values.links.filter((link) => link?.length);

    // Convert HTML content to plain text, slicing it to 300 characters for result_summary
    let result_summary = convertHtmlToText({
      htmlContent: formik.values.result,
    }).slice(0, 300);

    let keywords = formik.values.keywords;
    keywords = keywords?.map((item) => item.id);
    // Define keys to be excluded from the final result
    let excludeKeys = [
      "createdAt",
      "updatedAt",
      "_id",
      "__v",
      "links",
      "visible",
      "creator",
      "termsOfService",
      "project",
      "writers",
      "publishedAt",
      "result_summary",
      "keywords",
      "num_id",
      "likes",
    ];

    // Iterate over formik values and populate data object
    for (const [key, value] of Object.entries(formik.values)) {
      let isValueDefined = value || value?.length;
      let isKeyExcluded = excludeKeys.includes(key);

      if (isValueDefined && !isKeyExcluded) {
        data[key] = value;
      }
    }

    // Create the body object with data, links, result_summary, result
    body = {
      ...data,
      keywords,
      links,
      result_summary,
    };

    let saveRes;
    let editRes;

    try {
      // Check if publishing, then set loading state and call appropriate API endpoint
      if (isPublish) {
        setSaveAndPublishLoading(true);
        if (resultId) {
          editRes = await editResult({ body, id: resultId }).unwrap();
        } else {
          saveRes = await saveResult({ body, id: project_id_param }).unwrap();
        }
        setIsSaved(true);
        // Set result visibility to true and notify success
        let changeVisibilityResponse = await changeResultVisibility({
          id: resultId || saveRes?._id,
          body: { visible: true },
        }).unwrap();

        notify(changeVisibilityResponse?.message, "success");

        // Refetch project data and navigate to results page
        refetchProjectData();
        navigate(`/results/${resultId || saveRes?._id}`);
      } else {
        // If not publishing, call the appropriate API endpoint
        if (resultId) {
          editRes = await editResult({ body, id: resultId }).unwrap();
          notify(editRes?.message, "success");
          navigate(`/results/${resultId}`);
        } else {
          saveRes = await saveResult({ body, id: project_id_param }).unwrap();
          notify(saveRes?.message, "success");
        }

        // Trigger a get request for project data
        await triggerGetProjectData({
          id: project_id_param,
        }).unwrap();
      }
    } catch (error) {
      // Handle errors, displaying appropriate notifications
      handleError(error, null, true);
    }

    // Reset loading state
    setSaveAndPublishLoading(false);

    // Append result id to search params and set result id state if not publishing
    if (saveRes?._id && !isPublish) {
      searchParam.append("result-id", saveRes?._id);
      searchParam.delete("new");
      setSearchParam(searchParam);
      setResultId(saveRes?._id);
    }
  };

  const handleChangeCheckBox = (e, property, value) => {
    const prevTypes = [...formik.values.types];
    if (e.target.checked) {
      prevTypes.push(value);
      formik.setFieldValue(property, prevTypes);
    } else {
      const updatedTypes = prevTypes.filter((item) => item !== value);
      formik.setFieldValue(property, updatedTypes);
    }
  };
  if (!isSaved && (projectDataLoading || resultDataLoading)) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center fixed bg-custom-gray-100 top-0 left-0 z-50">
        <Bars
          height="50"
          width="50"
          color="#8538ff"
          ariaLabel="bars-loading"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
        />
      </div>
    );
  } else
    return (
      <div className="w-full flex flex-col items-center pt-36 ">
        <MetaTags
          title="Create Result"
          description={`NFT- Fundraising By FunDeSci An innovative approach for decentralized crowdfunding of scientific projects`}
          link="/create-result"
          noIndex
        />
        <section className="w-full flex flex-col justify-center items-center py-10 bg-secondary max-mlg:px-3">
          <h2 className="text-2xl mlg:text-3xl text-center abel-font font-semibold text-white tracking-wider">
            Sharing Insights and Inspiring Future Research
          </h2>
          <p className="text-white mt-4 text-center max-w-3xl text-lg mlg:text-xl leading-7">
            Publishing results promotes transparency in research. It allows the
            scientific community and the public to scrutinize, validate, and
            replicate findings.
          </p>
        </section>
        <Container className="flex flex-col items-center mt-10 ">
          <p className="mlg:text-lg mlg:leading-7">
            Publishing results allows for the dissemination of knowledge and
            findings, contributing to the advancement of scientific progress. It
            facilitates collaboration, enables researchers to build upon
            existing work, and ultimately propels innovations forward.
          </p>

          <h3 className="w-full text-left text-2xl font-bold abel-font mt-7">
            Project Info
          </h3>
          <div className="w-full flex flex-col lg:flex-row gap-y-5 justify-between mt-10">
            {/* //// */}
            <div className="flex flex-col w-full lg:max-w-[50%] xl:max-w-[50%]">
              <h5 className="text-lg text-custom-gray-900 poppins-font uppercase font-semibold mb-2">
                Details
              </h5>

              <div
                onClick={() => window.open(`/projects/${project?._id}`)}
                className="w-full flex p-3 rounded-2xl bg-custom-gray-200 cursor-pointer"
              >
                <img
                  className="hidden mlg:block w-16 h-16 mlg:w-44 mlg:h-44 rounded-2xl"
                  alt="abstract"
                  src={project?.abstract}
                />
                <div className="w-full flex flex-col justify-between mlg:ml-3 mlg:pb-2">
                  <div className="w-full flex items-start">
                    <img
                      className="mlg:hidden w-16 h-16 rounded-lg"
                      alt="abstract"
                      src={project?.abstract}
                    />
                    <div className="w-full flex flex-col max-mlg:pl-3 ">
                      <span className="text-xl font-bold">
                        {project?.title.slice(0, 40)}
                        {project?.title?.length > 40 ? "..." : ""}
                      </span>
                      <div className="flex w-full justify-between mt-1 text-xs mlg:text-sm">
                        <span className="text-custom-gray-600 ">
                          {project?.category}
                        </span>
                        <span className="text-custom-gray-600 ">
                          {project?.fundraising_method}
                        </span>
                      </div>{" "}
                    </div>
                  </div>

                  <div
                    className={`w-full flex justify-between items-center max-mlg:mt-2 `}
                  >
                    <div
                      onClick={(e) => {
                        e.stopPropagation();

                        navigateToProfile(
                          project?.creator?._id,
                          project?.creator?.role
                        );
                      }}
                      className="flex items-center cursor-pointer"
                    >
                      <span className=" rounded-full overflow-hidden flex justify-center items-center cursor-pointer h-9 w-9">
                        <img
                          src={project?.creator.images?.profile}
                          alt={"user-img"}
                          className="transition-all duration-300 hover:scale-110 w-full h-full"
                        />
                      </span>
                      <span className="flex flex-col ml-2 justify-between">
                        <span className="font-semibold text-sm ">
                          {project?.creator.fundername ||
                            project?.creator.firstname +
                              " " +
                              project?.creator.lastname}
                        </span>
                        <span className=" text-[#797979] text-xs truncate ">
                          {project?.creator?.title_rank ||
                            project?.creator?.location ||
                            project?.creator?.email}
                        </span>
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <CopyContent
                        value={project?.creator?.public_address}
                        className={`text-blue-link truncate`}
                      >
                        <WalletDisplay address={project?.creator?.public_address} />
                      </CopyContent>

                      <span className="text-xs text-primary font-bold uppercase">
                        creator
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* {resultData?.writers && ( */}
            <div
              className={`w-full lg:max-w-[50%] lg:ml-14 flex flex-col ${
                resultData?.writers ? "opacity-100" : "opacity-0"
              }`}
            >
              <>
                <h5 className="text-lg text-custom-gray-900 poppins-font uppercase font-semibold pl-2 mb-2">
                  Writers
                </h5>
                <div className="w-full flex flex-col rounded-2xl bg-custom-gray-200 max-h-48 overflow-auto custom-primary-tiny-scroll ">
                  {resultData?.writers?.length
                    ? resultData?.writers?.map((item, index, arr) => (
                        <div
                          key={index}
                          className={`w-full flex justify-between py-3 px-5  items-center cursor-pointer ${
                            index !== arr?.length - 1 &&
                            "border-b border-custom-gray-500"
                          }`}
                        >
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              item?.role !== UN_REGISTERED &&
                                navigateToProfile(item?._id, item?.role);
                            }}
                            className="flex items-center"
                          >
                            <span className=" rounded-full overflow-hidden flex justify-center items-center cursor-pointer h-9 w-9">
                              <img
                                src={item?.images?.profile}
                                alt={"user-img"}
                                className="transition-all duration-300 hover:scale-110 w-full h-full"
                              />
                            </span>
                            <span className="flex flex-col ml-2 justify-between">
                              <span className="font-semibold ">
                                {item?.fundername ||
                                  item?.firstname + " " + item?.lastname}
                              </span>
                              <span className=" text-[#797979] text-sm ">
                                {item?.title_rank ||
                                  item?.location ||
                                  item?.email}
                              </span>
                            </span>
                          </div>
                          <CopyContent
                            value={item?.public_address}
                            className={`text-blue-link truncate`}
                            id={2}
                          >
                            <WalletDisplay address={item?.public_address} />
                          </CopyContent>
                        </div>
                      ))
                    : null}
                </div>
              </>
            </div>
            {/* )} */}
          </div>

          <h3 className="w-full text-left text-2xl font-bold abel-font mt-10 mlg:mt-24">
            Result Info
          </h3>
          <form
            onSubmit={formik.handleSubmit}
            className="w-full flex flex-col mt-7 pb-20"
          >
            <section className="w-full flex flex-col md:flex-row justify-between mlg:h-[550px]  ">
              {/* result img */}
              <div className="w-full mlg:w-[50%] h-80 mlg:h-full relative">
                <div
                  id="feature_image"
                  className={`w-full h-full rounded-3xl bg-[#E1E0E3] flex flex-col justify-center items-center overflow-hidden ${
                    formik?.touched?.feature_image &&
                    formik?.errors?.feature_image &&
                    "border-2 border-red-500"
                  } `}
                >
                  {cropResultImage || formik.values.feature_image ? (
                    <div className="w-full h-full relative ">
                      {!formik?.values?.visible && (
                        <>
                          <label
                            onClick={() => {
                              if (
                                cropResultImage ||
                                formik.values.feature_image
                              )
                                setShowResultCropModal(true);
                            }}
                            className="w-9 h-9 p-1 flex justify-center items-center rounded-full bg-primary absolute right-3 top-3 cursor-pointer z-10"
                          >
                            <ProfileEdtiIcon />

                            <input
                              type="file"
                              className="hidden"
                              onChange={handleImageSelect}
                              accept={ACCEPT_IMAGE_FILES}
                              disabled={
                                cropResultImage || formik.values.feature_image
                              }
                            />
                          </label>

                          <button
                            className="w-9 h-9 flex justify-center items-center rounded-full absolute left-3 top-3 cursor-pointer z-10 bg-custom-gray-200"
                            onClick={() => {
                              setResultImg(null);
                              setCropResultImage(null);
                              formik?.setFieldValue("feature_image", "");
                            }}
                          >
                            <RemoveRowIcon />
                          </button>
                        </>
                      )}
                      <img
                        src={formik.values.feature_image || cropResultImage}
                        alt="feature_image"
                        className="w-full h-full object-cover transition-all duration-300 hover:scale-105"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full flex flex-col justify-center items-center">
                      <label
                        onClick={() => {
                          if (cropResultImage || formik.values.feature_image)
                            setShowResultCropModal(true);
                        }}
                        className="w-8 h-8 flex justify-center items-center rounded-full bg-white cursor-pointer"
                      >
                        <PlusIcon />
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleImageSelect}
                          accept={ACCEPT_IMAGE_FILES}
                        />
                      </label>
                      {/* //////// */}
                      <span className=" text-lg font-medium mt-2 roboto-font">
                        Add Result Photo
                      </span>

                      <span className="text-[#3C2166] mt-7  text-sm ">
                        File types: JPG, PNG, GIF. Max size: 2 MB
                      </span>
                    </div>
                  )}
                </div>
                <FieldErrorMsg formik={formik} id="feature_image" />
              </div>
              {/* result detail */}
              <div className="w-full mlg:w-[50%] flex flex-col h-full max-mlg:mt-3 mlg:ml-6 lg:ml-12">
                <TextField
                  id={"title"}
                  value={formik.values.title}
                  title={"Result Title"}
                  placeholder={
                    "Write a short and accurate title of your result"
                  }
                  containerClass="w-full"
                  inputClass="h-12"
                  formik={formik}
                  onBlur={formik.handleBlur}
                  required
                />
                <ExternalLinkField
                  id={"links"}
                  onChange={formik.handleChange}
                  value={formik.values.links}
                  title={"Link"}
                  description={
                    "Please provide external links to your results (Personal web page, Github, Kaggle, Dribble, ...)"
                  }
                  placeholder={"https://yoursite.io"}
                  containerClass="w-full mt-5"
                  inputClass=""
                  itemClass="h-12"
                  formik={formik}
                  onBlur={formik.handleBlur}
                />
                <InputTag
                  id={"keywords"}
                  formik={formik}
                  value={formik?.values?.keywords}
                  title={"Keyword"}
                  placeholder={"Add the most prominent keywords of your result"}
                  containerClass="w-full mt-3"
                  inputWrapperClass="min-h-[5rem] max-h-28 py-2.5"
                  suggestions={KEYWORD_SUGGESTIONS}
                  required
                />{" "}
                <div className="w-full flex flex-col mt-5">
                  <span className="font-bold text-sm ">Result Types:</span>
                  <div className="w-full flex flex-wrap gap-3 mt-3">
                    {resultTypes?.map((type, index) => (
                      <label key={type + index} className="">
                        <input
                          type="checkbox"
                          name="type"
                          className="mr-2 accent-primary scale-110"
                          checked={formik?.values?.types?.includes(type)}
                          onChange={(e) =>
                            handleChangeCheckBox(e, "types", type)
                          }
                        />
                        {type}
                      </label>
                    ))}{" "}
                  </div>
                </div>
                {formik && formik.touched?.types && formik.errors?.types && (
                  <span className="text-red-500 text-sm mt-1">
                    {formik.errors?.types}
                  </span>
                )}
              </div>
            </section>

            <section className="w-1/2 mt-5">
              <AttachFileField
                id={"attachment"}
                onChange={handleAttachFileChange}
                value={formik.values.attachment}
                title={"Supplementary Document"}
                placeholder={
                  "Attach supplementary document (PDF file up to 2 MB)"
                }
                containerClass="mt-5 w-full"
                inputClass="h-12"
                formik={formik}
                onBlur={formik.handleBlur}
                description="Attach supplementary document (PDF file up to 2 MB)"
                isLoading={uploadFileLoading}
              />

              <div className="w-full flex flex-col mt-5 pl-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="premium"
                    checked={formik.values.premium}
                    onChange={(e) =>
                      formik.setFieldValue("premium", e.target.checked)
                    }
                    className=" mr-2 accent-primary rounded-sm scale-150 "
                  />
                  <label htmlFor="premium" className=" mlg:text-lg">
                    Publish this result as premium content
                  </label>
                </div>
                {formik &&
                  formik.touched?.premium &&
                  formik.errors?.premium && (
                    <span className="text-red-500 text-sm mt-1">
                      {formik.errors?.premium}
                    </span>
                  )}
              </div>
            </section>

            <div className="w-full mt-20 mlg:mt-16 ">
              <LexicalEditor
                onChange={(value) => {
                  formik.setFieldValue("result", value);
                }}
                value={formik.values.result}
                placeholder="Outline the key results and findings obtained from your result. This should include any important observations, trends, or implications derived from the research."
                title="Results and Findings"
                id={"result"}
                containerClass="w-full mt-16"
                formik={formik}
                onBlur={formik.handleBlur}
                inputClass="h-52"
                limit={EDITOR_LIMITS.result}
                required={true}
                showImageBtn={true}
              />
            </div>

            <div className="w-full flex flex-col mlg:flex-row gap-y-5 justify-between mt-16">
              {!resultData?.visible && (
                <button
                  type="button"
                  onClick={async () => {
                    setIsPublish(false);
                    await formik?.submitForm();
                  }}
                  disabled={
                    editResultLoading ||
                    saveDraftLoading ||
                    saveAndPublishLoading ||
                    changeResultVisibilityLoading
                  }
                  className={` font-medium text-2xl px-5 save-draft-btn rounded-xl h-fit mt-1 `}
                >
                  {!saveAndPublishLoading &&
                  (editResultLoading || saveDraftLoading) ? (
                    <Oval
                      height={35}
                      width={35}
                      color="#8538FF"
                      wrapperStyle={{}}
                      wrapperClass=""
                      visible={true}
                      ariaLabel="oval-loading"
                      secondaryColor="#8538FF"
                      strokeWidth={2}
                      strokeWidthSecondary={2}
                    />
                  ) : resultId ? (
                    " Save Changes"
                  ) : (
                    " Save Draft"
                  )}
                </button>
              )}
              <div className="flex flex-col">
                <button
                  onClick={() => setIsPublish(true)}
                  type="submit"
                  disabled={
                    saveAndPublishLoading ||
                    changeResultVisibilityLoading ||
                    saveDraftLoading ||
                    editResultLoading
                  }
                  className="w-full mlg:w-72 h-10 bg-light-primary flex justify-center items-center min-w-[80px] text-white text-sm  rounded-lg disabled:cursor-not-allowed disabled:opacity-70 "
                >
                  {saveAndPublishLoading ||
                  changeResultVisibilityLoading ||
                  (resultData?.visible && editResultLoading) ? (
                    <Bars
                      height="25"
                      width="25"
                      color="#ffffff"
                      ariaLabel="bars-loading"
                      wrapperStyle={{}}
                      wrapperClass=""
                      visible={true}
                    />
                  ) : resultData && resultData?.visible ? (
                    "Save changes"
                  ) : (
                    "Publish"
                  )}
                </button>
                {!resultData?.visible ? (
                  <>
                    <div className="flex mt-5 w-full mlg:w-80 items-start">
                      <input
                        type="checkbox"
                        id="termsOfService"
                        checked={formik.values.termsOfService}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "termsOfService",
                            e.target.checked
                          )
                        }
                        className=" mt-1 mr-1 accent-primary rounded-sm  "
                      />
                      <label htmlFor="termsOfService" className=" text-sm">
                        By publishing my result, I agree to the{" "}
                        <ExternalLink
                          className="underline"
                          href="/terms-of-service"
                        >
                          terms of service
                        </ExternalLink>{" "}
                        and commit to following the rules set forth by the
                        platform.
                      </label>
                    </div>
                    {formik &&
                      formik.touched?.termsOfService &&
                      formik.errors?.termsOfService && (
                        <span className="text-red-500 text-sm mt-1">
                          {formik.errors?.termsOfService}
                        </span>
                      )}
                  </>
                ) : null}
              </div>
            </div>
          </form>
        </Container>
        {showResultCropModal && (
          <CropImageModal
            setShow={setShowResultCropModal}
            show={showResultCropModal}
            cropImage={cropResultImage}
            setCropImage={setCropResultImage}
            setImage={setResultImg}
            image={resultImg}
            type="project"
            id="feature_image"
            formik={formik}
          />
        )}
      </div>
    );
};

export default CreateResult;
